import React, { useEffect, useRef } from 'react';
import { useRegisterQuery } from 'services/freeTrial';
import {
  useFreeTrialContext,
  useFreeTrialPageNavigation,
} from 'providers/FreeTrialProvider';
import { useSnackbarForMessage } from 'providers/SnackbarProvider';
import { useValidateForm } from './FormPage';
import { useLocationSearch } from 'hooks/useLocationSearch';
import { Progress } from 'features/freeTrial';

export default function LandingPage() {
  const queryParams = useLocationSearch();

  const isValid = useValidateForm(queryParams);

  const query = useRegisterQuery(
    { body: queryParams },
    {
      skip: !isValid,
    }
  );

  useHandleRedirection(isValid, queryParams, query);

  // delay attribute purpose: Delay showing the loader if it's less than about 1 sec
  return (
    <Progress delay message="Registering your account, please hold tight..." />
  );
}

// ------------------------------------------------------------- //
// Redirection
// ------------------------------------------------------------- //

function useHandleRedirection(isValid, queryParams, query) {
  const hasRedirected = useRef(false);

  const { isSuccess, isError, data, error } = query;
  const { actions, meta } = useFreeTrialContext();
  const { announceSnackbar } = useSnackbarForMessage();
  const navigateToPage = useFreeTrialPageNavigation();

  const { registrationToken } = data ?? {};
  const errorMessage = error?.data?.title ?? 'Something went wrong';

  // Cleanup function to reset the hasRedirected flag back to false
  // While cleanup in this case is not necessary, it's good practice
  // It's not necessary really here due to the navigateToPage call
  // using replace instead of push on the history stack, but in theory
  // if anyone revistited this page, the cleanup would be necessary
  // to ensure the redirect happens again
  const cleanup = () => {
    hasRedirected.current = false;
  };

  const handleInvalidRegistration = () => {
    actions.setFormValues(queryParams, meta.pages.FAIL_SAFE_FORM);
    navigateToPage(meta.pages.FAIL_SAFE_FORM);
    announceSnackbar(
      'Almost there! Please complete all required fields to proceed.',
      'error'
    );
  };

  const handleError = () => {
    actions.setFormValues(queryParams, meta.pages.FAIL_SAFE_FORM);
    navigateToPage(
      error?.status === 400
        ? meta.pages.FAIL_SAFE_FORM
        : meta.pages.ERROR_DEFAULT
    );
    announceSnackbar(errorMessage, 'error');
  };

  const handleSuccess = () => {
    actions.setRegistrationToken(data.registrationToken);
    actions.setFormValues(
      {
        companyName: queryParams?.companyName ?? '',
        email: queryParams?.email ?? '',
      },
      meta.pages.BASIC_INFO_FORM
    );
    navigateToPage(meta.pages.CHECK_YOUR_EMAIL);
    announceSnackbar(
      `We've received your information. Please check your email for next steps.`,
      'success'
    );
  };

  useEffect(() => {
    // Guard against multiple redirects
    if (hasRedirected.current) {
      return cleanup();
    }

    // Need to run hasRedirected.current = true; in each statement
    // If we call before or after then the useEffect won't run again
    // Which is actually needed to listen for isSuccess, isError, etc.
    if (!isValid) {
      hasRedirected.current = true;
      handleInvalidRegistration();
    } else if (isError) {
      hasRedirected.current = true;
      handleError();
    } else if (isSuccess) {
      hasRedirected.current = true;
      handleSuccess();
    }

    return cleanup();
  }, [isValid, isSuccess, isError, registrationToken, errorMessage]); // Note: No references in dependencies!
}
